'use client';

import { useCallback, useEffect, useRef } from 'react';

interface UseSpcScrollPinParams {
  gnbType: string;
  isDesktop: boolean;
}

export const SPC_SCROLL_PIN_RECHECK_EVENT = 'spc-scroll-pin-recheck';

export function recheckSpcScrollPin(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SPC_SCROLL_PIN_RECHECK_EVENT));
}

function getPinLine(gnbType: string, isDesktop: boolean): number {
  if (isDesktop) return 0;

  return gnbType === 'Y'
    ? document.querySelector('#main-gnb-bar')?.clientHeight || 0
    : document.querySelector('#header .inner-header')?.clientHeight || 0;
}

function getNaviLiveBottom(fallback: number): number {
  return (
    document
      .querySelector('.corner-navi.fixed .event-navigation-type2')
      ?.getBoundingClientRect().bottom ?? fallback
  );
}

/** 스티키 탭에서 앵커 이동 시 GNB 재노출을 반영한 내비 하단 */
export function getSpcNaviRevealedBottom(
  gnbType: string,
  isDesktop: boolean
): number {
  const pinLine = getPinLine(gnbType, isDesktop);
  const naviHght = document.querySelector('.corner-navi')?.clientHeight || 0;
  const naviFixedBottom = pinLine + naviHght;

  if (gnbType !== 'Y') return naviFixedBottom;

  const headerHght = document.querySelector('#header')?.clientHeight || 0;
  const subGnbHght = document.querySelector('.sub-gnb-list')?.clientHeight || 0;

  return naviFixedBottom + headerHght + subGnbHght;
}

export default function useSpcScrollPin({
  gnbType,
  isDesktop,
}: UseSpcScrollPinParams): void {
  const brandListHeight = useRef(0);
  const brandTabTextOffset = useRef(0);

  const handleScroll = useCallback(() => {
    const pinLine = getPinLine(gnbType, isDesktop);

    const filterBar = document.querySelector(
      '.bottom-product-wrap .lp-filter-bar'
    );
    const filterRectTop = filterBar
      ? filterBar.getBoundingClientRect().top
      : Infinity;

    const navi = document.querySelector('.corner-navi');
    const naviHght = navi?.clientHeight || 0;
    // 브랜드 스티키 판정용(퍼블과 동일: pinLine + navi 높이)
    const brandStickyNaviBottomBase = pinLine + naviHght;

    if (navi) {
      const naviRectTop = navi.getBoundingClientRect().top;

      // 내비 양보 판정: gnbType=Y일 때 헤더/서브GNB 높이 포함
      let naviFixedBottom = brandStickyNaviBottomBase;
      if (gnbType === 'Y') {
        const headerHght = document.querySelector('#header')?.clientHeight || 0;
        const subGnbHght =
          document.querySelector('.sub-gnb-list')?.clientHeight || 0;
        naviFixedBottom = pinLine + headerHght + subGnbHght + naviHght;
      }
      const bottomReached = filterRectTop <= naviFixedBottom;

      if (naviRectTop <= pinLine && !bottomReached) {
        navi.classList.add('fixed');
      } else {
        navi.classList.remove('fixed');
      }
    }

    if (filterBar) {
      if (filterRectTop <= pinLine) {
        filterBar.classList.add('is-pinned');
      } else {
        filterBar.classList.remove('is-pinned');
      }
    }

    // 브랜드 탭 스티키(모바일 전용)
    // 시작: 브랜드 탭 텍스트 상단이 내비 하단에 닿을 때
    // 해제: 유지 구간 하단이 내비 하단을 지날 때
    if (isDesktop) return;

    const wrap = document.querySelector<HTMLElement>('.brand-list-wrap');
    const tabText = wrap?.querySelector('.brand-tab-item .text');
    // 유지 구간 끝: 인터뷰 영역, 인터뷰 없는 브랜드는 하이라이트 영역
    const stickyEnd =
      document.querySelector('.corner-interview') ??
      document.querySelector('.corner-brand-highlight');
    if (!wrap || !tabText || !stickyEnd) return;

    // 스티키 시 brand-list가 fixed로 빠지므로 평상시 측정값을 기억
    if (!wrap.classList.contains('is-sticky')) {
      brandListHeight.current = wrap.offsetHeight;
      brandTabTextOffset.current =
        tabText.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
    }

    const naviBottom = getNaviLiveBottom(brandStickyNaviBottomBase);
    const textTop =
      wrap.getBoundingClientRect().top + brandTabTextOffset.current;
    const reached = textTop <= naviBottom;
    const passed = stickyEnd.getBoundingClientRect().bottom <= naviBottom;

    const isSticky = wrap.classList.contains('is-sticky');
    const willBeSticky = reached && !passed;

    if (willBeSticky === isSticky) return;

    // 축약형 전환으로 탭 폭이 바뀌므로 보정 전 간격을 먼저 잼
    const list = wrap.querySelector<HTMLElement>('.items-scrollBar');
    const items = wrap.querySelectorAll<HTMLElement>('.brand-tab-item');
    const padLeft = items[0]?.offsetLeft ?? 0;
    const pitchBefore = items[1]
      ? items[1].offsetLeft - items[0].offsetLeft
      : 0;
    const scrollLeftBefore = list?.scrollLeft ?? 0;
    const maxBefore = list ? list.scrollWidth - list.clientWidth : 0;

    if (willBeSticky) {
      wrap.style.height = `${brandListHeight.current}px`;
      wrap.classList.add('is-sticky');
    } else {
      wrap.style.height = '';
      wrap.classList.remove('is-sticky');
    }

    const pitchAfter = items[1] ? items[1].offsetLeft - items[0].offsetLeft : 0;
    if (list && pitchBefore && pitchAfter && pitchBefore !== pitchAfter) {
      list.scrollLeft =
        scrollLeftBefore >= maxBefore - 1
          ? list.scrollWidth - list.clientWidth
          : (scrollLeftBefore - padLeft) * (pitchAfter / pitchBefore) + padLeft;
    }
  }, [gnbType, isDesktop]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener(SPC_SCROLL_PIN_RECHECK_EVENT, handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener(SPC_SCROLL_PIN_RECHECK_EVENT, handleScroll);
    };
  }, [handleScroll]);
}
